import { useEffect, useRef, useState } from 'react'
import { useAutoResizeTextarea } from '../useAutoResizeTextarea'
import Styles from './V4EditCampaign.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button'
import { Input } from 'almanac-ui/TextInputField/Input'
import { TopBar } from '../TopBar/TopBar'
import { V4StepFooter } from '../V4StepFooter'
import { useStepAction } from '../useStepAction'
import { useBrandPlanSchedule } from 'hooks/queries/schedules/useBrandPlanSchedule'
import { useUpdateBrandPlanSchedule } from 'hooks/queries/schedules/useUpdateBrandPlanSchedule'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { reportError } from 'utils/error'
import { Theme } from 'types'

interface Props {
  scheduleId: number
  onNext: () => void
  onBack: () => void
}

export const V4EditCampaign = ({ scheduleId, onNext, onBack }: Props) => {
  const { isMobile } = useResponsiveDesign()
  const { data: schedule, isLoading } = useBrandPlanSchedule(scheduleId, {
    refetchInterval: (data) => {
      if (data && !data.themes?.length && data.is_preparing) return 1000
      return false
    },
  })
  const { mutateAsync: updateSchedule } = useUpdateBrandPlanSchedule({ id: scheduleId })

  const [initialized, setInitialized] = useState(false)
  const [themes, setThemes] = useState<Theme[]>([])
  const [selectedThemeId, setSelectedThemeId] = useState<string | undefined>(undefined)
  const [campaignName, setCampaignName] = useState('')
  const [themeDescription, setThemeDescription] = useState('')
  const [callToAction, setCallToAction] = useState('')
  const [targetLink, setTargetLink] = useState('')
  const { isSaving, run } = useStepAction()
  const { ref: themeTextareaRef, resize: autoResizeTheme } = useAutoResizeTextarea(themeDescription)
  const emptyThemesReported = useRef(false)

  useEffect(() => {
    if (!schedule || !schedule.themes?.length || initialized) return
    setInitialized(true)
    setThemes(schedule.themes)
    const firstTheme = schedule.theme ?? schedule.themes[0]
    setSelectedThemeId(firstTheme?.id)
    setThemeDescription(firstTheme?.description ?? '')
    setCampaignName(schedule.title ?? '')
    setCallToAction(schedule.call_to_action ?? '')
    setTargetLink(schedule.target_link ?? '')
  }, [schedule, initialized])

  const handleNext = () =>
    run(async () => {
      if (!schedule) return
      const updatedThemes = themes.map((t) =>
        t.id === selectedThemeId ? { ...t, title: campaignName, description: themeDescription } : t,
      )
      await updateSchedule({
        theme: selectedThemeId,
        themes: updatedThemes,
        call_to_action: callToAction,
        target_link: targetLink,
      })
      onNext()
    }, 'Something went wrong saving your campaign. Please try again.')

  if (isLoading || !schedule) {
    return null
  }

  if (!schedule.themes?.length) {
    if (schedule.is_preparing) return null

    if (!emptyThemesReported.current) {
      emptyThemesReported.current = true
      reportError(new Error(`V4EditCampaign: schedule ${scheduleId} loaded with empty themes`))
    }
    return (
      <MultiStepStepLayout headingLevel={isMobile ? 2 : 1} scrollContainerClassName={Styles.scrollContainer}>
        <div className={Styles.content}>
          <TopBar highlightStep="campaign" />
          <div className={Styles.headerSection}>
            <Heading level={isMobile ? 2 : 1}>Something went wrong</Heading>
            <Text color="var(--dark-60)">
              We had trouble preparing your campaign details. Please go back and try again, or contact support if the
              issue persists.
            </Text>
          </div>
          <Button variant="tertiary" size="lg" onPress={onBack}>
            Go back
          </Button>
        </div>
      </MultiStepStepLayout>
    )
  }

  if (!initialized) {
    return null
  }

  return (
    <MultiStepStepLayout headingLevel={isMobile ? 2 : 1} scrollContainerClassName={Styles.scrollContainer}>
      <V4StepFooter onBack={onBack} onNext={handleNext} isSaving={isSaving}>
        <div className={Styles.content}>
          <TopBar highlightStep="campaign" />

          <div className={Styles.headerSection}>
            <Heading level={isMobile ? 2 : 1}>Here&apos;s your first campaign details</Heading>
            <Text color="var(--dark-60)">
              You can think of these as what directs the main subject and target of the content Blaze generates.
            </Text>
          </div>

          <hr className={Styles.divider} />

          <div className={Styles.fields}>
            <div className={Styles.field}>
              <Text variant="secondary" color="var(--dark-90)">
                Campaign Name
              </Text>
              <Input
                name="campaignName"
                size="xl"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="e.g. Summer Promotion Campaign"
              />
            </div>

            {themes.length > 0 && (
              <div className={Styles.field}>
                <Text variant="secondary" color="var(--dark-90)">
                  Theme
                </Text>
                <textarea
                  ref={themeTextareaRef}
                  className={Styles.themeTextarea}
                  value={themeDescription}
                  onChange={(e) => {
                    setThemeDescription(e.target.value)
                    autoResizeTheme()
                  }}
                  placeholder="Describe the theme for your campaign content"
                />
              </div>
            )}

            <div className={Styles.field}>
              <Text variant="secondary" color="var(--dark-90)">
                Call-to-Action <span className={Styles.optional}>(optional)</span>
              </Text>
              <Input
                name="callToAction"
                size="xl"
                value={callToAction}
                onChange={(e) => setCallToAction(e.target.value)}
                placeholder="e.g. Book a free consultation"
              />
            </div>

            <div className={Styles.field}>
              <Text variant="secondary" color="var(--dark-90)">
                Target Link <span className={Styles.optional}>(optional)</span>
              </Text>
              <Input
                name="targetLink"
                size="xl"
                value={targetLink}
                onChange={(e) => setTargetLink(e.target.value)}
                placeholder="https://"
              />
            </div>
          </div>
        </div>
      </V4StepFooter>
    </MultiStepStepLayout>
  )
}
